import { Button, Col, Progress, Row, Space } from 'antd';
import DashboardCard from 'components/DashboardCard';
import Page from 'components/Page';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import React from 'react';
import { Link } from 'react-router-dom';
import { EDIT_ACCESSIBILITY, EDIT_LICENSE } from 'shared/constants/permissions';
import { Module } from 'shared/prisma/types';
import { hasAccessibilityLicense } from 'shared/utils/license';
import { renderRelativeDate } from 'components/Table/renderers';
import { useAccessibilityReview } from './context';
import { SETUP_ACCESSIBILITY_REVIEW } from './graphql';
import { PageIntroduction, ReviewPage } from './ReviewPage';
import { syncStatus } from './tags';
import {
  DASHBOARD_EVIDENCE_SOURCES,
  nextScheduledScanPresentation,
  overviewActivationPresentation,
  overviewSourceFindingsPath,
  pageCoveragePresentation,
  scanOverviewSource,
} from './viewModel';

const OverviewCard = ({ title, extra, statistics, children, ...cardProps }) => (
  <DashboardCard
    module={Module.Accessibility}
    title={title}
    extra={extra}
    statistics={statistics}
    {...cardProps}
  >
    {children}
  </DashboardCard>
);

const sourceIsScheduled = {
  PROVIDER: (scope, _website, { licensed }) => licensed && Boolean(scope?.automaticRunsEnabled),
  PDF: (scope, _website, { licensed }) => licensed && scope?.pdfScanningEnabled !== false,
  BROKEN_LINKS: (_scope, website) => website?.brokenLinksEnabled !== false,
};

const ActivateAccessibility = () => {
  const { refetch } = useAccessibilityReview();
  const { dataSource, dataSourceId, hasDataSourcePermission } = useDataSource();
  const { hasPermission, isSuperUser } = useUser();
  const { message } = useGlobalContext();
  const licensed = hasAccessibilityLicense(dataSource?.activeLicense);
  const activation = overviewActivationPresentation({
    licensed,
    canManage: Boolean(hasPermission(EDIT_ACCESSIBILITY)),
    canEditLicense: Boolean(hasDataSourcePermission(EDIT_LICENSE)),
    isSuperUser,
  });
  const [setupReview, { loading }] = useMutation(SETUP_ACCESSIBILITY_REVIEW, {
    onCompleted: async () => {
      message.success(licensed
        ? 'Accessibility connected. Weekly Accessibility, PDF, and Broken Links scans are enabled.'
        : 'Accessibility connected for manual checks. Scheduled scans stay off.');
      await refetch();
    },
    onError: error => message.error(error.message),
  });
  const activate = () => setupReview({ variables: { dataSourceId: dataSource.id } });

  return (
    <Page.Content>
      <PageIntroduction title={activation.title}>
        <Space direction="vertical" size="middle">
          <div>{activation.text}</div>
          {activation.action === 'activate' && (
            <Button
              type="primary"
              loading={loading}
              disabled={!dataSource?.id}
              onClick={activate}
            >
              Activate accessibility
            </Button>
          )}
          {activation.action === 'upgrade' && (
            <Link to={`/data-sources/${dataSourceId}/settings/license`}>
              <Button type="primary">Upgrade license</Button>
            </Link>
          )}
          {activation.action === 'ask-owner' && (
            <div>Ask the owner to add Accessibility to the active license.</div>
          )}
          {activation.secondaryAction === 'activate-anyway' && (
            <Button
              loading={loading}
              disabled={!dataSource?.id}
              onClick={activate}
            >
              Activate anyway
            </Button>
          )}
        </Space>
      </PageIntroduction>
    </Page.Content>
  );
};

const SourceCard = ({ source, label, baseUrl, scope, website, scanOverview, loading, licensed }) => {
  const current = scanOverviewSource(scanOverview, source);
  const latestRun = current?.latestRun;
  const latestCompleted = current?.latestCompletedRun;
  const latestScanAt = latestRun?.createdAt || latestCompleted?.createdAt;
  const latestRunAt = latestRun?.completedAt || latestRun?.createdAt;
  const scheduled = sourceIsScheduled[source](scope, website, { licensed });
  const nextScan = scheduled
    ? nextScheduledScanPresentation({ checkInterval: website?.checkInterval, latestScanAt })
    : { text: 'Not scheduled' };
  const href = overviewSourceFindingsPath(source, baseUrl);

  return (
    <Col xs={24} md={12} xl={8}>
      <OverviewCard
        loading={loading}
        title={label}
        statistics={[{ title: 'Open observations', value: current?.observationCount || 0 }]}
        actions={[
          <Link key={source} to={href}>
            <Button type="link">View findings</Button>
          </Link>,
        ]}
      >
        <div style={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: 14, lineHeight: 1.7, marginTop: 16, textAlign: 'center' }}>
          {latestRun ? (
            <div>
              Latest scan
              {' '}
              {syncStatus(latestRun.status)}
              {' '}
              {renderRelativeDate(latestRunAt) || '—'}
            </div>
          ) : (
            <div>No scan recorded yet.</div>
          )}
          <div>
            Next scan
            {' '}
            {nextScan.at ? renderRelativeDate(nextScan.at) : (nextScan.text || '—')}
          </div>
        </div>
      </OverviewCard>
    </Col>
  );
};

const Overview = () => {
  const { baseUrl, scope, website, shellLoading } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const licensed = hasAccessibilityLicense(dataSource?.activeLicense);
  const scanOverview = scope?.scanOverview;
  const completedReviewUnits = scanOverview?.completedReviewUnitCount || 0;
  const reviewUnitCount = scanOverview?.reviewUnitCount || 0;
  const coverage = pageCoveragePresentation({
    allAffectedPageCount: scanOverview?.pagesWithObservations,
    inventoriedPageCount: scanOverview?.inventoriedPageCount,
  });

  return (
    <ReviewPage title="Accessibility overview">
      {!shellLoading && !scope ? <ActivateAccessibility /> : (
        <>
          <Row gutter={[16, 16]}>
            {DASHBOARD_EVIDENCE_SOURCES.map(({ source, label }) => (
              <SourceCard
                key={source}
                source={source}
                label={label}
                baseUrl={baseUrl}
                scope={scope}
                website={website}
                scanOverview={scanOverview}
                loading={shellLoading}
                licensed={licensed}
              />
            ))}
            <Col xs={24} md={12} xl={8}>
              <OverviewCard
                loading={shellLoading}
                title="Page coverage"
                statistics={[
                  { title: 'Pages with findings', value: coverage.pagesWithFindings },
                  { title: 'Inventoried pages', value: coverage.inventoriedPages ?? '…' },
                ]}
                actions={[
                  <Link key="pages" to={`${baseUrl}/pages`}>
                    <Button type="link">View pages</Button>
                  </Link>,
                ]}
              />
            </Col>
            <Col xs={24} md={12} xl={8}>
              <OverviewCard
                loading={shellLoading}
                title="Manual review"
                statistics={[{
                  title: 'Completed',
                  value: completedReviewUnits,
                  suffix: `/ ${reviewUnitCount}`,
                }]}
                actions={[
                  <Link key="manual-review" to={`${baseUrl}/manual-review`}>
                    <Button type="link">Open review</Button>
                  </Link>,
                ]}
              >
                <Progress
                  percent={reviewUnitCount ? Math.round((completedReviewUnits / reviewUnitCount) * 100) : 0}
                  size="small"
                  showInfo={false}
                  style={{ marginTop: 12 }}
                />
              </OverviewCard>
            </Col>
          </Row>
        </>
      )}
    </ReviewPage>
  );
};

export default Overview;
